import * as Sentry from '@sentry/tanstackstart-react';
import { QueryClient } from '@tanstack/react-query';
import { createRouter as createTanStackRouter } from '@tanstack/react-router';
import { setupRouterSsrQueryIntegration } from '@tanstack/react-router-ssr-query';
import * as React from 'react';
import { LoadingDots } from '@_/ui';
import { retryOnlyIfStatus500 } from '@_/utils-frontend';
import { App } from '#src/App.tsx';
import { DefaultErrorBoundary } from '#src/pages/errorPages/DefaultErrorBoundary.tsx';
import { NotFoundPage } from '#src/pages/errorPages/NotFoundPage.tsx';
import { initStores } from '#src/state/initStores.ts';
import { routeTree } from './routeTree.gen.ts';

export function getRouter() {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        retry: retryOnlyIfStatus500
      }
    }
  });

  const { useAuthStore } = initStores({ queryClient });

  const router = createTanStackRouter({
    routeTree,
    context: {
      queryClient,
      useAuthStore
    },
    defaultErrorComponent: props => (
      <App>
        <DefaultErrorBoundary {...props} />
      </App>
    ),
    defaultNotFoundComponent: () => (
      <App>
        <NotFoundPage />
      </App>
    ),
    defaultPendingComponent: () => <LoadingDots fullPage />,
    defaultPreload: false,
    defaultPreloadStaleTime: 0,
    defaultStructuralSharing: true,
    notFoundMode: 'root',
    scrollRestoration: true
  });

  setupRouterSsrQueryIntegration({
    router,
    queryClient
  });

  if (!router.isServer) {
    if (import.meta.env.ENV_SENTRY_DSN) {
      Sentry.init({
        dsn: import.meta.env.ENV_SENTRY_DSN,
        environment: import.meta.env.MODE,
        integrations: [Sentry.tanstackRouterBrowserTracingIntegration(router)],
        tracesSampleRate: 1.0
      });
    } else {
      /* oxlint-disable-next-line no-console */
      console.warn('ENV_SENTRY_DSN is not defined. Sentry is not running.');
    }
  }

  return router;
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof getRouter>;
  }
}
